import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CreditCard, Wallet, Landmark, Loader2, CheckCircle2, Copy, ArrowLeft, Lock, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import { getRefCode } from "../lib/referral";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fmtPrice } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const COUNTRIES = [
  { code: "CA", label: "Canada" },
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "OTHER", label: "Other" },
];

const METHODS = [
  { id: "card", label: "Credit / Debit Card", desc: "Secure checkout via Stripe", icon: CreditCard },
  { id: "paypal", label: "PayPal", desc: "Pay with PayPal balance or card", icon: Wallet },
  { id: "etransfer", label: "Interac e-Transfer", desc: "Canadian orders only", icon: Landmark },
];

const inputCls = "w-full bg-[#0F0F10] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] block mb-1.5";

const emptyAddress = { full_name: "", phone: "", street: "", apartment: "", city: "", province: "", postal_code: "", country: "CA" };

function StepDot({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-mono ${done ? "bg-[#4ADE80] text-[#0F0F10]" : active ? "bg-[#4ADE80]/20 text-[#4ADE80] border border-[#4ADE80]" : "bg-white/5 text-[#7a7a7a] border border-white/10"}`}>
        {done ? <Check className="h-3.5 w-3.5" /> : n}
      </div>
      <span className={`text-[10px] uppercase tracking-[0.16em] ${active || done ? "text-[#F5F5F5]" : "text-[#7a7a7a]"}`}>{label}</span>
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal, unitPriceOf, clearCart } = useCart();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 contact, 2 shipping, 3 payment
  const [method, setMethod] = useState("card");
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [useNewAddress, setUseNewAddress] = useState(true);

  const [contact, setContact] = useState({ email: "", name: "", createAccount: false, password: "" });
  const [address, setAddress] = useState(emptyAddress);
  const [saveAddress, setSaveAddress] = useState(true);

  // Discount code state
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null); // { code, discount_amount, total }
  const [discountBusy, setDiscountBusy] = useState(false);

  // Re-validate discount whenever the cart contents change
  useEffect(() => {
    if (!discount) return;
    if (items.length === 0) { setDiscount(null); return; }
    api.post("/discounts/validate", {
      code: discount.code,
      items: items.map((i) => ({ product_id: i.slug, quantity: i.quantity })),
    }).then(({ data }) => setDiscount(data)).catch(() => setDiscount(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, subtotal]);

  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setPaypalCfg(data)).catch(() => setPaypalCfg({ configured: false }));
  }, []);

  useEffect(() => {
    if (user && user.user_id) {
      setContact((c) => ({ ...c, email: user.email || "", name: user.name || "", createAccount: false }));
      api.get("/account/addresses").then(({ data }) => {
        setSavedAddresses(data || []);
        const def = (data || []).find((a) => a.is_default_shipping) || (data || [])[0];
        if (def) {
          setUseNewAddress(false);
          setAddress({
            full_name: def.full_name || user.name || "",
            phone: def.phone || "",
            street: def.street || "",
            apartment: def.apartment || "",
            city: def.city || "",
            province: def.province || "",
            postal_code: def.postal_code || "",
            country: (def.country || "").toUpperCase().slice(0, 2) === "CA" ? "CA" :
                     (def.country || "").toUpperCase().slice(0, 2) === "US" ? "US" : "OTHER",
          });
        } else {
          setAddress((a) => ({ ...a, full_name: user.name || "" }));
        }
      }).catch(() => {});
    }
  }, [user]);

  // Auto-select payment method based on country
  useEffect(() => {
    if (address.country !== "CA" && method === "etransfer") setMethod("card");
  }, [address.country, method]);

  const cartPayload = () => ({ items: items.map((i) => ({ product_id: i.slug, quantity: i.quantity })) });

  const applyDiscount = async () => {
    const code = (discountInput || "").trim().toUpperCase();
    if (!code) return;
    setDiscountBusy(true);
    try {
      const { data } = await api.post("/discounts/validate", { code, ...cartPayload() });
      setDiscount(data);
      toast.success(`Code ${code} applied — you saved ${fmtPrice(data.discount_amount)}`);
    } catch (e) {
      setDiscount(null);
      toast.error(e.response?.data?.detail || "Invalid discount code");
    } finally {
      setDiscountBusy(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountInput("");
  };

  const discountAmount = discount?.discount_amount || 0;
  const orderTotal = Math.max(0, subtotal - discountAmount);

  const customerPayload = () => ({
    email: contact.email,
    name: contact.name,
    phone: address.phone,
    shipping_address: address,
    create_account: !user?.user_id && contact.createAccount,
    password: !user?.user_id && contact.createAccount ? contact.password : undefined,
    save_address: !!user?.user_id && saveAddress && useNewAddress,
    discount_code: discount?.code || undefined,
    ref_code: getRefCode() || undefined,
  });

  const contactValid = () => {
    if (!contact.email.trim()) return false;
    if (!user?.user_id && contact.createAccount && (contact.password || "").length < 8) return false;
    return true;
  };

  const shippingValid = () => (
    address.full_name.trim() && address.street.trim() && address.city.trim() &&
    address.province.trim() && address.postal_code.trim() && address.country
  );

  const applySavedAddress = (id) => {
    const a = savedAddresses.find((x) => x.id === id);
    if (!a) return;
    setUseNewAddress(false);
    setAddress({
      full_name: a.full_name || "", phone: a.phone || "",
      street: a.street || "", apartment: a.apartment || "",
      city: a.city || "", province: a.province || "",
      postal_code: a.postal_code || "",
      country: (a.country || "").toUpperCase().slice(0, 2) === "CA" ? "CA" :
               (a.country || "").toUpperCase().slice(0, 2) === "US" ? "US" : "OTHER",
    });
  };

  const payCard = async () => {
    if (!contactValid() || !shippingValid()) { toast.error("Please complete all required fields"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", { ...cartPayload(), ...customerPayload(), origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
      else throw new Error();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to start card checkout.");
      setBusy(false);
    }
  };

  const submitEtransfer = async () => {
    if (!contactValid() || !shippingValid()) { toast.error("Please complete all required fields"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/etransfer/order", {
        ...cartPayload(),
        name: contact.name,
        email: contact.email,
        country: "CA",
        phone: address.phone,
        shipping_address: address,
        create_account: !user?.user_id && contact.createAccount,
        password: !user?.user_id && contact.createAccount ? contact.password : undefined,
        save_address: !!user?.user_id && saveAddress && useNewAddress,
        discount_code: discount?.code || undefined,
        ref_code: getRefCode() || undefined,
      });
      clearCart();
      if (contact.createAccount) refreshUser?.();
      setComplete({ method: "etransfer", ...data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to create e-Transfer order.");
    } finally { setBusy(false); }
  };

  const copyRef = () => {
    if (complete?.reference) { navigator.clipboard?.writeText(complete.reference); toast.success("Reference copied."); }
  };

  /* ---- Complete screen ---- */
  if (complete) {
    return (
      <div className="pt-16 min-h-screen flex flex-col bg-[#0F0F10]">
        <section className="mx-auto max-w-[720px] w-full px-5 sm:px-8 py-20 flex-1">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} data-testid="checkout-complete" className="border border-white/10 bg-[#1F1F1F] p-8 sm:p-10">
            <CheckCircle2 className="h-12 w-12 text-[#4ADE80]" />
            <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight">{complete.method === "etransfer" ? "Order Placed" : "Payment Complete"}</h1>
            {complete.method === "etransfer" ? (
              <>
                <p className="mt-4 text-[#A8A8A8]">Send your Interac e-Transfer to complete the order. It ships once payment is received.</p>
                <div className="mt-8 border border-white/10 divide-y divide-white/10 text-sm">
                  <div className="flex justify-between px-5 py-4"><span className="text-[#7a7a7a] uppercase text-[10px] tracking-widest">Send to</span><span className="text-[#4ADE80]">{complete.etransfer_email}</span></div>
                  <div className="flex justify-between px-5 py-4"><span className="text-[#7a7a7a] uppercase text-[10px] tracking-widest">Amount</span><span>{fmtPrice(complete.amount)} {complete.currency}</span></div>
                  <div className="flex justify-between items-center px-5 py-4">
                    <span className="text-[#7a7a7a] uppercase text-[10px] tracking-widest">Reference</span>
                    <button onClick={copyRef} data-testid="copy-reference" className="flex items-center gap-2 hover:text-[#4ADE80]">{complete.reference} <Copy className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <p className="mt-6 text-xs text-[#7a7a7a] leading-relaxed">Include reference <span className="text-[#4ADE80]">{complete.reference}</span> in the e-Transfer message field.</p>
              </>
            ) : (
              <p className="mt-4 text-[#A8A8A8]">Thank you for your order. A confirmation has been sent to your email.</p>
            )}
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/products" className="btn-press bg-[#4ADE80] text-[#0F0F10] px-8 py-3.5 hover:bg-[#6FE998] uppercase tracking-[0.14em] text-[11px]">Continue Browsing</Link>
              <Link to="/account" className="border border-white/10 px-8 py-3.5 hover:bg-white/5 uppercase tracking-[0.14em] text-[11px] text-[#F5F5F5]">View Account</Link>
            </div>
          </motion.div>
        </section>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pt-16 min-h-screen flex flex-col bg-[#0F0F10]">
        <section className="mx-auto max-w-[720px] w-full px-5 sm:px-8 py-24 flex-1">
          <div className="border border-white/10 p-16 text-center">
            <p data-testid="checkout-empty" className="text-[#7a7a7a] uppercase tracking-widest text-sm">Your cart is empty</p>
            <Link to="/products" className="mt-8 inline-block btn-press bg-[#4ADE80] text-[#0F0F10] px-8 py-3.5 hover:bg-[#6FE998] uppercase tracking-[0.14em] text-[11px]">Browse Catalog</Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen flex flex-col bg-[#0F0F10]">
      <section className="mx-auto max-w-[1200px] w-full px-4 sm:px-8 py-10 sm:py-14 flex-1">
        <Link to="/cart" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Cart
        </Link>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight">Checkout</motion.h1>

        {/* Progress */}
        <div className="mt-8 flex flex-wrap items-center gap-3 sm:gap-6">
          <StepDot n="1" label="Contact" active={step === 1} done={step > 1} />
          <div className="hidden sm:block h-px w-8 bg-white/10" />
          <StepDot n="2" label="Shipping" active={step === 2} done={step > 2} />
          <div className="hidden sm:block h-px w-8 bg-white/10" />
          <StepDot n="3" label="Payment" active={step === 3} done={false} />
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 lg:gap-10">
          {/* Left column — steps */}
          <div className="space-y-6">
            {/* STEP 1 — CONTACT */}
            <section data-testid="checkout-step-contact" className={`border p-6 sm:p-8 transition-colors ${step === 1 ? "border-white/20 bg-[#1F1F1F]" : "border-white/10 bg-[#171717]"}`}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-xl text-[#F5F5F5]">Contact</h2>
                {step !== 1 && <button onClick={() => setStep(1)} className="text-[10px] uppercase tracking-[0.16em] text-[#4ADE80] hover:text-[#6FE998]">Edit</button>}
              </div>
              {step === 1 ? (
                <div className="space-y-4">
                  {user?.user_id ? (
                    <div className="border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#A8A8A8]">
                      Signed in as <span className="text-[#F5F5F5]">{user.email}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-white/10 bg-black/20 px-4 py-3 text-xs">
                      <span className="text-[#A8A8A8]"><span className="text-[#4ADE80]">✓</span> No account needed — check out as a guest</span>
                      <Link to={`/login?redirect=/checkout`} className="text-[#4ADE80] hover:text-[#6FE998] uppercase tracking-[0.14em]">Have an account? Sign in</Link>
                    </div>
                  )}
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input data-testid="checkout-email" required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls} placeholder="you@example.com" disabled={!!user?.user_id} />
                  </div>
                  <div>
                    <label className={labelCls}>Full Name *</label>
                    <input data-testid="checkout-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputCls} placeholder="Alex Researcher" />
                  </div>
                  {!user?.user_id && (
                    <div className="pt-2 border-t border-white/5">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input data-testid="checkout-create-account" type="checkbox" checked={contact.createAccount} onChange={(e) => setContact({ ...contact, createAccount: e.target.checked })} className="mt-1 accent-[#4ADE80]" />
                        <span className="text-sm text-[#E5E5E5] flex items-center gap-2"><UserPlus className="h-4 w-4 text-[#4ADE80]" /> <span><span className="text-[#F5F5F5]">Optional:</span> save an account for faster reorders &amp; order tracking</span></span>
                      </label>
                      {contact.createAccount && (
                        <div className="mt-3">
                          <label className={labelCls}>Password (min 8 chars)</label>
                          <input data-testid="checkout-password" type="password" value={contact.password} onChange={(e) => setContact({ ...contact, password: e.target.value })} className={inputCls} placeholder="••••••••" minLength={8} />
                        </div>
                      )}
                    </div>
                  )}
                  <button data-testid="checkout-continue-contact" onClick={() => contactValid() && setStep(2)} disabled={!contactValid()} className="btn-press bg-[#4ADE80] text-[#0F0F10] px-8 py-3.5 hover:bg-[#6FE998] uppercase tracking-[0.14em] text-[11px] disabled:opacity-40 disabled:cursor-not-allowed">Continue to Shipping</button>
                </div>
              ) : (
                <div className="text-sm text-[#A8A8A8]">{contact.name} · {contact.email}{contact.createAccount && " · Account will be created"}</div>
              )}
            </section>

            {/* STEP 2 — SHIPPING */}
            <section data-testid="checkout-step-shipping" className={`border p-6 sm:p-8 transition-colors ${step === 2 ? "border-white/20 bg-[#1F1F1F]" : "border-white/10 bg-[#171717]"} ${step < 2 ? "opacity-50 pointer-events-none" : ""}`}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-xl text-[#F5F5F5]">Shipping Address</h2>
                {step > 2 && <button onClick={() => setStep(2)} className="text-[10px] uppercase tracking-[0.16em] text-[#4ADE80] hover:text-[#6FE998]">Edit</button>}
              </div>
              {step === 2 ? (
                <div className="space-y-4">
                  {savedAddresses.length > 0 && (
                    <div className="border border-white/10 bg-black/20 p-4">
                      <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8A8A8] mb-3">Saved addresses</div>
                      <div className="space-y-2">
                        {savedAddresses.map((a) => (
                          <button key={a.id} data-testid={`saved-addr-${a.id}`} onClick={() => applySavedAddress(a.id)} className={`w-full text-left border px-4 py-3 text-sm transition-colors ${!useNewAddress && address.street === a.street ? "border-[#4ADE80] bg-[#4ADE80]/5" : "border-white/10 hover:border-white/25"}`}>
                            <div className="text-[#F5F5F5]">{a.full_name} · {a.label}</div>
                            <div className="text-xs text-[#A8A8A8] mt-0.5">{a.street}{a.apartment ? `, ${a.apartment}` : ""}, {a.city}, {a.province} {a.postal_code}</div>
                          </button>
                        ))}
                        <button data-testid="use-new-address" onClick={() => { setUseNewAddress(true); setAddress({ ...emptyAddress, full_name: contact.name }); }} className={`w-full text-left border px-4 py-3 text-sm transition-colors ${useNewAddress ? "border-[#4ADE80] bg-[#4ADE80]/5" : "border-white/10 hover:border-white/25"}`}>+ Use a new address</button>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <input data-testid="ship-full-name" required value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Phone</label>
                      <input data-testid="ship-phone" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className={inputCls} placeholder="+1 555 000 0000" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Street Address *</label>
                    <input data-testid="ship-street" required value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Apt / Suite / Unit</label>
                    <input data-testid="ship-apt" value={address.apartment} onChange={(e) => setAddress({ ...address, apartment: e.target.value })} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>City *</label>
                      <input data-testid="ship-city" required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Province / State *</label>
                      <input data-testid="ship-province" required value={address.province} onChange={(e) => setAddress({ ...address, province: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Postal / Zip Code *</label>
                      <input data-testid="ship-postal" required value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Country *</label>
                      <Select value={address.country} onValueChange={(v) => setAddress({ ...address, country: v })}>
                        <SelectTrigger data-testid="ship-country" className="w-full bg-[#0F0F10] border-white/10 rounded-none text-sm h-[46px]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1F1F1F] border-white/10 text-[#F5F5F5]">
                          {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {user?.user_id && useNewAddress && (
                    <label className="flex items-center gap-3 cursor-pointer pt-2">
                      <input data-testid="ship-save" type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} className="accent-[#4ADE80]" />
                      <span className="text-sm text-[#E5E5E5]">Save this address to my account</span>
                    </label>
                  )}
                  <button data-testid="checkout-continue-shipping" onClick={() => shippingValid() && setStep(3)} disabled={!shippingValid()} className="btn-press bg-[#4ADE80] text-[#0F0F10] px-8 py-3.5 hover:bg-[#6FE998] uppercase tracking-[0.14em] text-[11px] disabled:opacity-40 disabled:cursor-not-allowed">Continue to Payment</button>
                </div>
              ) : step > 2 ? (
                <div className="text-sm text-[#A8A8A8]">{address.full_name} · {address.street}{address.apartment ? ", " + address.apartment : ""}, {address.city}, {address.province} {address.postal_code}, {address.country}</div>
              ) : null}
            </section>

            {/* STEP 3 — PAYMENT */}
            <section data-testid="checkout-step-payment" className={`border p-6 sm:p-8 transition-colors ${step === 3 ? "border-white/20 bg-[#1F1F1F]" : "border-white/10 bg-[#171717]"} ${step < 3 ? "opacity-50 pointer-events-none" : ""}`}>
              <h2 className="font-display text-xl text-[#F5F5F5] mb-5">Payment Method</h2>
              {step === 3 && (
                <>
                  <div className="space-y-2.5 mb-6" data-testid="payment-methods">
                    {METHODS.map((m) => {
                      const disabled = m.id === "etransfer" && address.country !== "CA";
                      const active = method === m.id;
                      return (
                        <button key={m.id} data-testid={`method-${m.id}`} onClick={() => !disabled && setMethod(m.id)} disabled={disabled}
                          className={`w-full flex items-center gap-4 border px-5 py-4 text-left transition-colors ${active ? "border-[#4ADE80] bg-[#4ADE80]/5" : "border-white/10 hover:border-white/25"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                          <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-[#4ADE80]" : "border-white/25"}`}>{active && <span className="h-2 w-2 rounded-full bg-[#4ADE80]" />}</span>
                          <m.icon className="h-5 w-5 text-[#E5E5E5]" strokeWidth={1.5} />
                          <span className="flex-1">
                            <span className="font-display text-sm text-[#F5F5F5] block">{m.label}</span>
                            <span className="text-[11px] text-[#7a7a7a]">{disabled ? "Set country to Canada to enable" : m.desc}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="border border-white/10 p-6 bg-black/20">
                    {method === "card" && (
                      <>
                        <p className="text-[#A8A8A8] text-sm mb-5">You&apos;ll be redirected to Stripe&apos;s secure checkout to complete your payment.</p>
                        <button data-testid="pay-card-btn" onClick={payCard} disabled={busy} className="w-full btn-press bg-[#4ADE80] text-[#0F0F10] py-4 hover:bg-[#6FE998] flex items-center justify-center gap-2 uppercase tracking-[0.14em] text-[11px] disabled:opacity-50">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {busy ? "Redirecting…" : `Pay ${fmtPrice(orderTotal)} by Card`}
                        </button>
                      </>
                    )}
                    {method === "paypal" && (
                      <div data-testid="paypal-panel">
                        {paypalCfg === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#4ADE80]" /></div>
                          : paypalCfg.configured ? (
                            <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: "USD", intent: "capture" }}>
                              <PayPalButtons style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                                createOrder={async () => {
                                  const { data } = await api.post("/paypal/create-order", { ...cartPayload(), ...customerPayload() });
                                  return data.id;
                                }}
                                onApprove={async (d) => {
                                  try {
                                    const res = await api.post(`/paypal/capture-order/${d.orderID}`);
                                    if (res.data.paid) { clearCart(); if (contact.createAccount) refreshUser?.(); setComplete({ method: "paypal" }); }
                                    else toast.error("Payment was not completed.");
                                  } catch { toast.error("Could not verify PayPal payment."); }
                                }}
                                onError={() => toast.error("PayPal encountered an error. Please try again.")} />
                            </PayPalScriptProvider>
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-[#E5E5E5]">PayPal isn&apos;t set up yet</p>
                              <p className="mt-2 text-xs text-[#7a7a7a]">Please use another payment method for now.</p>
                            </div>
                          )}
                      </div>
                    )}
                    {method === "etransfer" && (
                      <div>
                        <p className="text-[#A8A8A8] text-sm mb-5">We&apos;ll generate a reference and email you Interac e-Transfer instructions.</p>
                        <button data-testid="etransfer-submit" onClick={submitEtransfer} disabled={busy} className="w-full btn-press bg-[#4ADE80] text-[#0F0F10] py-4 hover:bg-[#6FE998] flex items-center justify-center gap-2 uppercase tracking-[0.14em] text-[11px] disabled:opacity-50">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {busy ? "Creating order…" : "Place e-Transfer Order"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>

          {/* Right column — order summary */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="border border-white/10 bg-[#1F1F1F] p-6">
              <h3 className="font-display text-lg text-[#F5F5F5] border-b border-white/10 pb-4">Order Summary</h3>
              <div className="mt-4 space-y-3 max-h-80 overflow-y-auto">
                {items.map((i) => (
                  <div key={i.slug} data-testid={`summary-item-${i.slug}`} className="flex justify-between gap-3 text-sm">
                    <span className="text-[#A8A8A8] truncate">{i.name} <span className="text-[#7a7a7a]">×{i.quantity}</span></span>
                    <span className="text-[#F5F5F5] whitespace-nowrap">{fmtPrice(unitPriceOf(i) * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-[#A8A8A8]">Subtotal</span><span className="text-[#F5F5F5]">{fmtPrice(subtotal)}</span></div>
                {discount && (
                  <div className="flex justify-between items-center text-sm" data-testid="checkout-discount-row">
                    <span className="text-[#4ADE80] flex items-center gap-2">
                      <span className="uppercase tracking-widest text-[10px]">Discount</span>
                      <span className="font-mono text-xs bg-[#4ADE80]/10 px-2 py-0.5 rounded">{discount.code}</span>
                      <button data-testid="checkout-discount-remove" onClick={removeDiscount} className="text-[#7a7a7a] hover:text-red-400 text-xs">✕</button>
                    </span>
                    <span className="text-[#4ADE80]">−{fmtPrice(discount.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm"><span className="text-[#A8A8A8]">Shipping</span><span className="text-[#7a7a7a] text-xs">Calculated at fulfillment</span></div>
                <div className="flex justify-between items-baseline pt-2 border-t border-white/5">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8]">Total</span>
                  <span data-testid="checkout-total" className="font-display text-3xl text-[#F5F5F5]">{fmtPrice(orderTotal)}</span>
                </div>
              </div>

              {/* Discount code input */}
              {!discount && (
                <div className="mt-5 pt-5 border-t border-white/5">
                  <label className="text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8]">Discount code</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      data-testid="checkout-discount-input"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyDiscount())}
                      placeholder="Enter code"
                      className="flex-1 bg-transparent border border-white/20 px-3 py-2.5 text-sm font-mono uppercase tracking-widest text-[#F5F5F5] outline-none focus:border-[#4ADE80]"
                    />
                    <button
                      data-testid="checkout-discount-apply"
                      onClick={applyDiscount}
                      disabled={discountBusy || !discountInput.trim()}
                      className="btn-press border border-white/20 text-[#F5F5F5] uppercase tracking-[0.14em] text-[11px] px-4 hover:border-[#4ADE80] hover:text-[#4ADE80] disabled:opacity-50"
                    >
                      {discountBusy ? "…" : "Apply"}
                    </button>
                  </div>
                </div>
              )}
              <p className="mt-4 text-[10px] text-[#7a7a7a] leading-relaxed text-center">Research use only. Not for human consumption.</p>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
